import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import ApiServices from "../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQrcode,
  faDownload,
  faCopy,
  faShareNodes,
  faRotateRight,
  faTriangleExclamation,
  faExpand,
  faTimes,
  faPrint,
  faShieldHalved,
  faLock,
  faChevronDown,
  faChevronUp,
  faCode,
  faCheck,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { AppKeys } from "./AppKeys";
import { encryptData } from "../../utils/encryption";
import "./ShareQrData.css";

/**
 * ShareQrData Component
 * Public shared component & page for generating and presenting encrypted QR code data.
 */
const ShareQrData = ({
  info: propInfo,
  data: propData,
  payload: propPayload,
  title = "رمز QR للمشاركة السريعة",
  subtitle = "امسح الرمز بواسطة الهاتف للتأكد والوصول للبيانات المشفرة",
  theme = "light",
  showActions = true,
  showDataPreview = false,
  fullPage,
  className = "",
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [encryptedInfo, setEncryptedInfo] = useState("");
  const [rawPayload, setRawPayload] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedData, setCopiedData] = useState(false);

  // Auto detect if rendered as standalone public page or nested component
  const isStandalonePage = useMemo(() => {
    if (typeof fullPage === "boolean") return fullPage;
    return (
      location.pathname.startsWith("/share-qr") ||
      location.pathname.startsWith("/share-qr-data")
    );
  }, [fullPage, location.pathname]);

  // Resolve input data source (props or query params)
  const resolvedInputData = useMemo(() => {
    // 1. Direct props
    const directData = propInfo ?? propData ?? propPayload;
    if (directData !== undefined && directData !== null) {
      return directData;
    }

    // 2. Query param 'info' or 'data' or 'payload'
    const urlInfo = searchParams.get("info") || searchParams.get("data") || searchParams.get("payload");
    if (urlInfo) {
      return urlInfo;
    }

    // Default fallback demo data
    return {
      appName: "EMt7any App",
      timestamp: Date.now(),
      verification: "Official Authorized Document",
    };
  }, [propInfo, propData, propPayload, searchParams]);

  const cardTitle = useMemo(() => {
    const paramTitle = searchParams.get("title");
    if (paramTitle) return paramTitle;
    return title;
  }, [title, searchParams]);

  const cardSubtitle = useMemo(() => {
    const paramSubtitle = searchParams.get("subtitle");
    if (paramSubtitle) return paramSubtitle;
    return subtitle;
  }, [subtitle, searchParams]);

  // Main QR Generation logic
  const fetchQrCode = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let encryptedString = "";

      // Check if resolved input is already an encrypted string or needs encryption
      if (typeof resolvedInputData === "string" && resolvedInputData.length > 30 && !resolvedInputData.includes(" ")) {
        // Assume already encrypted string passed
        encryptedString = resolvedInputData;
        setRawPayload(resolvedInputData);
      } else {
        // Dynamic object / primitive - perform encryption via encryptData utility
        setRawPayload(resolvedInputData);
        encryptedString = encryptData(resolvedInputData);
      }

      if (!encryptedString) {
        throw new Error("تشفير البيانات فشل، يرجى التأكد من صحة المدخلات.");
      }

      setEncryptedInfo(encryptedString);

      // Call API using ApiServices
      let base64Result = await ApiServices.get(
        "QrCodeEngine/GenerateQrCodeAsBase64",
        { info: encryptedString },
        200000,
        {
          skipDecrypt: true,
          headers: {
            "Accept": "*/*",
            "Accept-Language": "en",
          },
        }
      );

      if (!base64Result) {
        throw new Error("لم يتم استلام رمز QR من الخادم.");
      }

      // Ensure proper base64 data URI format
      if (typeof base64Result === "string" && !base64Result.startsWith("data:image")) {
        base64Result = `data:image/png;base64,${base64Result}`;
      }

      setQrImage(base64Result);
    } catch (err) {
      console.error("ShareQrData Error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "حدث خطأ أثناء إنشاء رمز QR. يرجى المحاولة لاحقاً.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [resolvedInputData]);

  useEffect(() => {
    fetchQrCode();
  }, [fetchQrCode]);

  // Action handlers
  const handleDownload = () => {
    if (!qrImage) return;
    try {
      const link = document.createElement("a");
      link.href = qrImage;
      link.download = `emthany-qr-code-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("تم تحميل رمز QR بنجاح! 📥");
    } catch (e) {
      toast.error("تعذر تحميل الرمز");
    }
  };

  const handleCopyLink = () => {
    try {
      const currentUrl = window.location.href;
      navigator.clipboard.writeText(currentUrl);
      setCopiedLink(true);
      toast.success("تم نسخ رابط المشاركة! 🔗");
      setTimeout(() => setCopiedLink(false), 2500);
    } catch (e) {
      toast.error("فشل نسخ الرابط");
    }
  };

  const handleCopyData = () => {
    try {
      const textToCopy =
        typeof rawPayload === "object"
          ? JSON.stringify(rawPayload, null, 2)
          : String(rawPayload || encryptedInfo);
      navigator.clipboard.writeText(textToCopy);
      setCopiedData(true);
      toast.success("تم نسخ البيانات المشفرة! 📋");
      setTimeout(() => setCopiedData(false), 2500);
    } catch (e) {
      toast.error("فشل النسخ");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Card Inner Render
  const renderCardContent = () => (
    <motion.div
      className={`share-qr-card ${theme === "light" ? "light-mode" : ""} ${className}`}
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Header Section */}
      <div className="share-qr-header">
        <div className="share-qr-badge">
          <FontAwesomeIcon icon={faShieldHalved} />
          <span>منصة امتحاني • رمز مشفر آمن</span>
        </div>
        <h2 className="share-qr-title">{cardTitle}</h2>
        <p className="share-qr-subtitle">{cardSubtitle}</p>
      </div>

      {/* Main Image Frame Section */}
      {loading ? (
        <div className="share-qr-loader-container">
          <div className="share-qr-spinner"></div>
          <span className="share-qr-loader-text">جاري تشفير البيانات وتوليد QR...</span>
        </div>
      ) : error ? (
        <div className="share-qr-error-container">
          <div className="share-qr-error-icon">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </div>
          <h3 className="share-qr-error-title">تعذر عرض رمز QR</h3>
          <p className="share-qr-error-desc">{error}</p>
          <button className="share-qr-retry-btn" onClick={fetchQrCode}>
            <FontAwesomeIcon icon={faRotateRight} />
            <span>إعادة المحاولة</span>
          </button>
        </div>
      ) : (
        <div
          className="share-qr-image-frame"
          onClick={() => setIsModalOpen(true)}
          title="انقر لتكبير الرمز"
        >
          {/* Laser Corner accents */}
          <div className="qr-corner-accent qr-corner-tl"></div>
          <div className="qr-corner-accent qr-corner-tr"></div>
          <div className="qr-corner-accent qr-corner-bl"></div>
          <div className="qr-corner-accent qr-corner-br"></div>

          {/* Scanning Line Animation */}
          <div className="qr-scan-bar"></div>

          {/* Generated QR Code Image */}
          <motion.img
            src={qrImage}
            alt="Generated QR Code"
            className="share-qr-img"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          />

          {/* Center Platform Logo */}
          <div className="share-qr-center-logo">
            <img src="/assets/images/new_log_design.svg" alt="EMt7any Logo" />
          </div>
        </div>
      )}

      {/* Action Buttons Grid */}
      {showActions && !loading && !error && (
        <div className="share-qr-actions">
          <button
            className="share-qr-btn share-qr-btn-primary"
            onClick={handleDownload}
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>تحميل الرمز</span>
          </button>

          <button
            className="share-qr-btn share-qr-btn-secondary"
            onClick={handleCopyLink}
          >
            <FontAwesomeIcon icon={copiedLink ? faCheck : faShareNodes} />
            <span>{copiedLink ? "تم النسخ!" : "مشاركة الرابط"}</span>
          </button>
        </div>
      )}





      {/* Security Footer Note */}
      <div className="share-qr-footer">
        <FontAwesomeIcon icon={faLock} />
        <span>محمي بنظام التشفير القياسي • EMt7any QrCode Engine</span>
      </div>
    </motion.div>
  );

  return (
    <>
      {isStandalonePage ? (
        <div className="share-qr-page-wrapper">
          {renderCardContent()}
        </div>
      ) : (
        <div className="share-qr-container">{renderCardContent()}</div>
      )}

      {/* Zoom / Fullscreen Modal */}
      <AnimatePresence>
        {isModalOpen && qrImage && (
          <motion.div
            className="share-qr-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="share-qr-modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="share-qr-modal-close"
                onClick={() => setIsModalOpen(false)}
                title="إغلاق"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={qrImage}
                  alt="Enlarged QR Code"
                  className="share-qr-modal-img"
                />
                <div className="share-qr-center-logo modal-center-logo">
                  <img src="/assets/images/new_log_design.svg" alt="EMt7any Logo" />
                </div>
              </div>
              <p
                style={{
                  marginTop: "1rem",
                  color: "#334155",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                رمز QR عالي الدقة
              </p>
              <button
                className="share-qr-btn share-qr-btn-primary"
                style={{ marginTop: "0.5rem" }}
                onClick={handleDownload}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>تحميل الرمز</span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShareQrData;
